import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import type { VerificationItem, OFACCheck, FdicCheck, SignableNote } from '@/components/verification-document/types';
import { useAxios } from './use-axios';
import { usePersistedState } from './use-persisted-state';

const API_BASE_URL = '/deals';

// Function to create default verification items for a deal
export function createDefaultItems(deal: Deal): VerificationItem[] {
  const ofacChecks: OFACCheck[] = [];
  const fdicChecks: FdicCheck[] = [];
  // Keep track of entity names already added to OFAC checks
  const existingOfacEntities = new Set<string>();

  // Add OFAC checks for people and their organizations
  if (deal.registrants.people?.length > 0) {
    for (const person of deal.registrants.people) {
      // Add OFAC check for person if not already added
      if (!existingOfacEntities.has(person.name)) {
        ofacChecks.push({
          id: `ofac-${person.email}`,
          type: 'ofac',
          entityName: person.name,
          searchResults: null,
          verified: false,
          signatures: [],
        });
        existingOfacEntities.add(person.name);
      }

      // If person has organization name, add OFAC check for org if not already added
      if (person.organization_name && !existingOfacEntities.has(person.organization_name)) {
        ofacChecks.push({
          id: `ofac-org-${person.organization_name}`,
          type: 'ofac',
          entityName: person.organization_name,
          searchResults: null,
          verified: false,
          signatures: [],
        });
        existingOfacEntities.add(person.organization_name);

        // If org has FDIC license, add FDIC check
        const fdicLicense = person.organization_licenses?.find(
          (license) => license.authority === 'FDIC',
        );
        if (fdicLicense) {
          fdicChecks.push({
            id: `fdic-org-${person.organization_name}`,
            type: 'fdic',
            entityName: person.organization_name,
            certNumber: fdicLicense.value,
            searchResults: null,
            verified: false,
            signatures: [],
          });
        }
      }
    }
  }

  let notes: SignableNote[] = [
    {
      id: 'org-eligibility',
      type: 'note',
      title: 'Organization Eligibility',
      content: '',
      signatures: [],
    },
    {
      id: 'email-eligibility',
      type: 'note',
      title: 'Email Eligibility',
      content: '',
      signatures: [],
    },
    {
      id: 'org-address',
      type: 'note',
      title: 'Organization Address',
      content: '',
      signatures: [],
    },
    {
      id: 'name-selection',
      type: 'note',
      title: 'Name Selection',
      content: '',
      signatures: [],
    },
    {
      id: 'employment-authority',
      type: 'note',
      title: 'Employment Authority',
      content: '',
      signatures: [],
    },
    {
      id: 'alt-contacts',
      type: 'note',
      title: 'Alt Contacts',
      content: '',
      signatures: [],
    },
  ];

  // Filter out specific notes for 'New Business' deals
  if (deal.deal_type === 'New Business') {
    notes = notes.filter((note) => note.id !== 'note' && note.id !== 'alt-contacts');
  }

  return [...ofacChecks, ...fdicChecks, ...notes];
}

// Function to ensure required checks are present
export function ensureRequiredChecks(deal: Deal, existingItems: VerificationItem[]): VerificationItem[] {
  // Check if there's a Marketing Information note
  const hasMarketingInfo = existingItems.some(
    (item) => item.type === 'note' && item.title === 'Marketing Information',
  );

  // Check if there's a Note item
  const hasNote = existingItems.some(
    (item) => item.type === 'note' && item.title.toLowerCase() === 'note',
  );

  // Create items to add if needed
  const itemsToAdd: VerificationItem[] = [];

  // Create a default note if one doesn't exist, unless it's a 'New Business' deal
  if (!hasNote && deal.deal_type !== 'New Business') {
    itemsToAdd.push({
      id: 'verification-note',
      type: 'note',
      title: 'Note',
      content: '',
      signatures: [],
    });
  }

  // If any items need to be added, add them in the correct order
  if (itemsToAdd.length > 0) {
    // If there's a Marketing Information note, find its index and insert after it
    if (hasMarketingInfo) {
      const marketingIndex = existingItems.findIndex(
        (item) => item.type === 'note' && item.title === 'Marketing Information',
      );
      existingItems = [
        ...existingItems.slice(0, marketingIndex + 1),
        ...itemsToAdd,
        ...existingItems.slice(marketingIndex + 1),
      ];
    } else {
      // If no Marketing Information, just add at the beginning
      existingItems = [...itemsToAdd, ...existingItems];
    }
  }

  // If any OFAC or FDIC checks exist, return items unchanged
  if (existingItems.some((item) => item.type === 'ofac' || item.type === 'fdic' || item.type === 'gleif')) {
    return existingItems;
  }

  // Generate required checks
  const newChecks: VerificationItem[] = [];
  // Keep track of entity names already added to OFAC checks
  const existingOfacEntities = new Set<string>();

  // Add OFAC checks for people and their organizations
  if (deal.registrants.people?.length > 0) {
    for (const person of deal.registrants.people) {
      // Add OFAC check for person if not already added
      if (!existingOfacEntities.has(person.name)) {
        newChecks.push({
          id: `ofac-${person.email}`,
          type: 'ofac',
          entityName: person.name,
          searchResults: null,
          verified: false,
          signatures: [],
        });
        existingOfacEntities.add(person.name);
      }

      // If person has organization name, add OFAC check for org if not already added
      if (person.organization_name && !existingOfacEntities.has(person.organization_name)) {
        newChecks.push({
          id: `ofac-org-${person.organization_name}`,
          type: 'ofac',
          entityName: person.organization_name,
          searchResults: null,
          verified: false,
          signatures: [],
        });
        existingOfacEntities.add(person.organization_name);

        // If org has FDIC license, add FDIC check
        const fdicLicense = person.organization_licenses?.find(
          (license) => license.authority === 'FDIC',
        );
        if (fdicLicense) {
          newChecks.push({
            id: `fdic-org-${person.organization_name}`,
            type: 'fdic',
            entityName: person.organization_name,
            certNumber: fdicLicense.value,
            searchResults: null,
            verified: false,
            signatures: [],
          });
        }
      }
    }
  }

  // Find the index after Marketing Info (if it exists) or Note
  const insertIndex = hasMarketingInfo
    ? existingItems.findIndex((item) => item.type === 'note' && item.title === 'Marketing Information') + 1
    : existingItems.findIndex((item) => item.type === 'note' && item.title.toLowerCase() === 'note') + 1;

  // Insert checks after Marketing Info or Note
  return [
    ...existingItems.slice(0, insertIndex),
    ...newChecks,
    ...existingItems.slice(insertIndex),
  ];
}

// Function to enforce specific ordering of verification items
export function enforceVerificationOrder(deal: Deal, items: VerificationItem[]): VerificationItem[] {
  // Create a new array to store items in the correct order
  const orderedItems: VerificationItem[] = [];
  const remainingItems = [...items];

  // Helper function to find and move an item
  const findAndMove = (predicate: (item: VerificationItem) => boolean): VerificationItem | undefined => {
    const index = remainingItems.findIndex((item) => predicate(item));
    if (index !== -1) {
      const [item] = remainingItems.splice(index, 1);
      return item;
    }
    return undefined;
  };

  // 0. Find and move "Marketing Information" to the very top
  const isMarketingInfo = (item: VerificationItem) =>
    item.type === 'note' && item.title === 'Marketing Information';
  const marketingInfoItem = findAndMove(isMarketingInfo);
  if (marketingInfoItem)
    orderedItems.push(marketingInfoItem);

  // 1. Find and move "Note" below Marketing Information (if not 'New Business' or if it exists)
  const isNote = (item: VerificationItem) =>
    item.type === 'note' && item.title.toLowerCase() === 'note';
  const noteIndex = remainingItems.findIndex((item) => isNote(item));
  if (deal.deal_type !== 'New Business' || noteIndex !== -1) {
    const noteItem = findAndMove(isNote);
    if (noteItem)
      orderedItems.push(noteItem);
  }

  // 2. Find and move "Additional Notes" below "Note"
  const isAdditionalNotes = (item: VerificationItem) =>
    item.type === 'note' && item.title.toLowerCase() === 'additional notes';
  const additionalNotesItem = findAndMove(isAdditionalNotes);
  if (additionalNotesItem)
    orderedItems.push(additionalNotesItem);

  // 3. Find and move "Forced Verification Note" below other top notes
  const isForcedVerificationNote = (item: VerificationItem) =>
    item.type === 'note' && item.title === 'Forced Verification Note';
  const forcedVerificationNoteItem = findAndMove(isForcedVerificationNote);
  if (forcedVerificationNoteItem)
    orderedItems.push(forcedVerificationNoteItem);

  // 3.5 Find and move "Imported GLEIF Info" note or similar LEI/GLEIF notes before OFAC checks
  const isImportedGLEIFInfo = (item: VerificationItem) =>
    item.type === 'note' && item.title === 'Imported GLEIF Info';

  const isLEIOrGLEIFNote = (item: VerificationItem) =>
    item.type === 'note'
    && (item.title.toLowerCase().includes('lei') || item.title.toLowerCase().includes('gleif'));

  // First try to find exact "Imported GLEIF Info"
  let gleifRelatedItem = findAndMove(isImportedGLEIFInfo);

  // If not found, look for any note containing LEI or GLEIF
  if (!gleifRelatedItem) {
    gleifRelatedItem = findAndMove(isLEIOrGLEIFNote);
  }

  // Add it to ordered items if found
  if (gleifRelatedItem) {
    orderedItems.push(gleifRelatedItem);
  }

  // Place any GLEIF checks immediately after the related note
  const gleifChecks = remainingItems.filter((item) => item.type === 'gleif');
  if (gleifChecks.length > 0) {
    remainingItems.splice(0, remainingItems.length, ...remainingItems.filter((item) =>
      item.type !== 'gleif',
    ));
    orderedItems.push(...gleifChecks);
  }

  // 4. Find and move OFAC org checks before OFAC person checks
  const ofacOrgChecks = remainingItems.filter((item) =>
    item.type === 'ofac' && item.id.includes('ofac-org-'),
  );
  const ofacPersonChecks = remainingItems.filter((item) =>
    item.type === 'ofac' && !item.id.includes('ofac-org-'),
  );
  remainingItems.splice(0, remainingItems.length, ...remainingItems.filter((item) =>
    item.type !== 'ofac',
  ));
  orderedItems.push(...ofacOrgChecks, ...ofacPersonChecks);

  // 5. Find and move FDIC check above Organization Eligibility
  const isFdicCheck = (item: VerificationItem) => item.type === 'fdic';
  const fdicChecks = findAndMove(isFdicCheck);

  const orgEligibilityIndex = remainingItems.findIndex((item) =>
    item.type === 'note' && item.title === 'Organization Eligibility',
  );

  if (orgEligibilityIndex !== -1) {
    if (fdicChecks) {
      orderedItems.push(fdicChecks);
    }
    orderedItems.push(remainingItems[orgEligibilityIndex]);
    remainingItems.splice(orgEligibilityIndex, 1);
  } else if (fdicChecks) {
    orderedItems.push(fdicChecks);
  }

  // 6. Find and move Email Eligibility
  const emailEligibilityIndex = remainingItems.findIndex((item) =>
    item.type === 'note' && item.title === 'Email Eligibility',
  );
  if (emailEligibilityIndex !== -1) {
    orderedItems.push(remainingItems[emailEligibilityIndex]);
    remainingItems.splice(emailEligibilityIndex, 1);
  }

  // 7. Find and move Organization Address
  const orgAddressIndex = remainingItems.findIndex((item) =>
    item.type === 'note' && item.title === 'Organization Address',
  );
  if (orgAddressIndex !== -1) {
    orderedItems.push(remainingItems[orgAddressIndex]);
    remainingItems.splice(orgAddressIndex, 1);
  }

  // 8. Find Name Selection and any trademark notes
  const nameSelectionIndex = remainingItems.findIndex((item) =>
    item.type === 'note' && item.title === 'Name Selection',
  );

  if (nameSelectionIndex !== -1) {
    orderedItems.push(remainingItems[nameSelectionIndex]);
    remainingItems.splice(nameSelectionIndex, 1);

    // Find and move trademark notes below Name Selection
    const trademarkNotes = remainingItems.filter((item) =>
      item.type === 'note' && item.title.toLowerCase().includes('trademark'),
    );
    remainingItems.splice(0, remainingItems.length, ...remainingItems.filter((item) =>
      !(item.type === 'note' && item.title.toLowerCase().includes('trademark')),
    ));
    orderedItems.push(...trademarkNotes);

    // Find and move use restriction notes below trademark (or Name Selection if no trademark)
    const useRestrictionNotes = remainingItems.filter((item) =>
      item.type === 'note' && item.title.toLowerCase().includes('use restriction'),
    );
    remainingItems.splice(0, remainingItems.length, ...remainingItems.filter((item) =>
      !(item.type === 'note' && item.title.toLowerCase().includes('use restriction')),
    ));
    orderedItems.push(...useRestrictionNotes);
  }

  // 9. Find and move Employment Authority
  const employmentAuthorityIndex = remainingItems.findIndex((item) =>
    item.type === 'note' && item.title === 'Employment Authority',
  );
  if (employmentAuthorityIndex !== -1) {
    orderedItems.push(remainingItems[employmentAuthorityIndex]);
    remainingItems.splice(employmentAuthorityIndex, 1);
  }

  // Add remaining items in their current order
  orderedItems.push(...remainingItems);

  // Ensure Alt Contacts exists and is at the bottom, unless it's a 'New Business' deal
  const hasAltContacts = orderedItems.some((item) =>
    item.type === 'note' && item.title === 'Alt Contacts',
  );

  if (!hasAltContacts && deal.deal_type !== 'New Business') {
    orderedItems.push({
      id: 'alt-contacts',
      type: 'note',
      title: 'Alt Contacts',
      content: '',
      signatures: [],
    });
  }

  return orderedItems;
}

export const DEALS_QUERY_KEY = ['deals'];

interface ApiError {
  error?: {
    message?: string;
    status?: number;
  };
  details?: Array<{ message: string }>;
}

/** The verification document changed on the server since this copy was loaded. */
export class VerificationConflictError extends Error {
  constructor(public readonly deal: Deal) {
    super('The verification document was changed in another tab or by another user.');
    this.name = 'VerificationConflictError';
  }
}

function toVerificationConflict(error: unknown): VerificationConflictError | null {
  if (!isAxiosError<{ code?: string; deal?: Deal }>(error))
    return null;
  const data = error.response?.data;
  if (error.response?.status === 409 && data?.code === 'verification_document_conflict' && data.deal)
    return new VerificationConflictError(data.deal);
  return null;
}

function getErrorMessage(error: unknown): string {
  if (isAxiosError<ApiError>(error)) {
    // Handle validation errors
    if (error.response?.data?.details) {
      return 'Please check your input and try again';
    }

    // Handle specific error codes
    if (error.response?.status === 500) {
      return 'An internal server error occurred. Please try again later';
    }

    if (error.response?.status === 401 || error.response?.status === 403) {
      return 'You do not have permission to perform this action';
    }

    if (error.response?.status === 404) {
      return 'The requested resource was not found';
    }

    // Handle network errors
    if (error.message === 'Network Error') {
      return 'Unable to connect to the server. Please check your internet connection';
    }
  }

  return 'An unexpected error occurred. Please try again';
}

export type DealType
  = | 'New Business'
    | 'Existing Business'
    | 'Annual Verification'
    | 'Forced Verification'
    | string; // Allow for future additions

export type DealOutcome
  = | 'Approved'
    | 'Combined'
    | 'Consolidation'
    | 'Did not Register'
    | 'Domain Deleted'
    | 'Duplicate'
    | 'fTLD Domain'
    | 'Incomplete'
    | 'Ineligible'
    | 'Junk'
    | 'Name Selection'
    | 'No Renew'
    | 'No Reply'
    | 'No Verification Needed'
    | 'Not Wanted'
    | 'Organization'
    | 'Price'
    | 'Test'
    | string; // Allow for future additions

export type DealStage
  = | 'Annual Verification'
    | 'Pending Verification'
    | 'Organization Verification in Progress'
    | 'Name Selection in Progress'
    | 'Employment Verification in Progress'
    | 'WHOIS Update in Progress'
    | 'Failed Verification'
    | 'Hold'
    | 'Verification Completed'
    | 'Verified Data Sent'
    | 'Completed'
    | string; // Allow for future additions

interface License {
  authority: string;
  value: string;
  contact_ids: string[];
  license_ids: string[];
  domains: string[];
}

interface Person {
  name: string;
  legal_name: string;
  alt_name: string | null;
  email: string;
  alt_email: string | null;
  phone_number: string;
  alt_phone: string | null;
  fax_number: string | null;
  address_line_1: string;
  address_line_2: string | null;
  address_line_3: string | null;
  city: string;
  state_province: string;
  postal_code: string;
  country: string;
  title: string;
  organization_name: string;
  c_suite: boolean;
  sole_prop: boolean;
  new_legal_name: boolean;
  contact_ids: string[];
  associated_domains: string[];
  domain_contact_map?: Record<string, string>;
  organization_licenses?: License[];
}

interface VerificationContact extends Person {
  organization_name: string;
}

export interface Deal {
  id: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
  verification_document: {
    body: string;
  } | null;
  deal_stage: DealStage;
  deal_type: DealType;
  outcome: DealOutcome | null;
  assigned_to: string;
  domains: string[];
  archived_at: string | null;
  partner_id: string | null;
  partner: {
    name: string;
    email: string;
    iana_id: string;
  } | null;
  registrants: {
    people: Person[];
  };
  verification_contacts: {
    people: VerificationContact[];
  };
}

export interface CreateDealInput {
  verification_document?: {
    body: string;
  } | null;
  deal_stage: DealStage;
  deal_type?: DealType;
  outcome?: DealOutcome | null;
  assigned_to: string;
  domains: string[];
}

export interface UpdateDealInput {
  verification_document?: {
    body: string;
  } | null;
  /** Required with verification_document: fingerprint of the stored body the edit started from. */
  expected_verification_hash?: string;
  deal_stage?: DealStage;
  deal_type?: DealType;
  outcome?: DealOutcome | null;
  assigned_to?: string;
  domains?: string[];
  archived_at?: string | null;
  partner_id?: string | null;
  registrants?: {
    people: Array<{
      name: string;
      legal_name: string;
      alt_name: string | null;
      email: string;
      alt_email: string | null;
      phone_number: string;
      alt_phone: string | null;
      fax_number: string | null;
      address_line_1: string;
      address_line_2: string | null;
      address_line_3: string | null;
      city: string;
      state_province: string;
      postal_code: string;
      country: string;
      title: string;
      c_suite: boolean;
      sole_prop: boolean;
      new_legal_name: boolean;
      contact_ids: string[];
    }>;
    organizations: Array<{
      name: string;
      email: string;
      phone_number: string;
      alt_phone: string | null;
      fax_number: string | null;
      address_line_1: string;
      address_line_2: string | null;
      address_line_3: string | null;
      city: string;
      state_province: string;
      postal_code: string;
      country: string;
      contact_ids: string[];
      licenses: Array<{
        authority: string;
        value: string;
        contact_ids: string[];
        license_ids: string[];
      }>;
    }>;
  };
  verification_contacts?: {
    people: Array<{
      name: string;
      legal_name: string;
      alt_name: string | null;
      email: string;
      alt_email: string | null;
      phone_number: string;
      alt_phone: string | null;
      fax_number: string | null;
      address_line_1: string;
      address_line_2: string | null;
      address_line_3: string | null;
      city: string;
      state_province: string;
      postal_code: string;
      country: string;
      title: string;
      c_suite: boolean;
      sole_prop: boolean;
      new_legal_name: boolean;
      organization_name: string;
      contact_ids: string[];
    }>;
  };
}

// Type for minimal deal data to store in localStorage
interface MinimalDeal {
  id: string;
  deal_stage: DealStage;
  deal_type: DealType;
  outcome: DealOutcome | null;
  assigned_to: string;
  domains: string[];
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

function dealToMinimal(deal: Deal): MinimalDeal {
  return {
    id: deal.id,
    deal_stage: deal.deal_stage,
    deal_type: deal.deal_type,
    outcome: deal.outcome,
    assigned_to: deal.assigned_to,
    domains: deal.domains,
    archived_at: deal.archived_at,
    created_at: deal.created_at,
    updated_at: deal.updated_at,
  };
}

// List all deals
export function useDeals() {
  const axios = useAxios();
  const [persistedDeals, setPersistedDeals] = usePersistedState<MinimalDeal[]>('cached-deals', []);

  return useQuery({
    queryKey: DEALS_QUERY_KEY,
    queryFn: async () => {
      try {
        const { data } = await axios.get<Deal[]>(API_BASE_URL);
        // Filter out archived deals (where archived_at is truthy)
        const filteredDeals = data.filter((deal) => !deal.archived_at);
        // Update persisted deals with minimal data
        setPersistedDeals(filteredDeals.map((deal) => dealToMinimal(deal)));
        return filteredDeals;
      } catch (error) {
        // On error, return persisted deals if available
        if (persistedDeals.length > 0) {
          return persistedDeals as Deal[];
        }
        throw new Error(getErrorMessage(error));
      }
    },
    // Keep data cached indefinitely
    gcTime: Infinity,
    // Always refetch in background when component mounts
    refetchOnMount: 'always',
    // Disable window focus refetching since we have interval
    refetchOnWindowFocus: false,
    // Refetch every 2 minutes while page is visible
    refetchInterval: 2 * 60 * 1000,
    refetchIntervalInBackground: false,
    // Initialize with persisted data
    initialData: persistedDeals as Deal[],
  });
}

// Get a single deal by ID. `fresh` refetches whenever the caller mounts, so it
// never works from a copy cached earlier in the session.
export function useDeal(id: string, { fresh = false }: { fresh?: boolean } = {}) {
  const axios = useAxios();

  return useQuery({
    queryKey: [...DEALS_QUERY_KEY, id],
    queryFn: async () => {
      try {
        const { data } = await axios.get<Deal>(`${API_BASE_URL}/${id}`);
        return data;
      } catch (error) {
        throw new Error(getErrorMessage(error));
      }
    },
    enabled: !!id,
    // Keep data cached indefinitely
    gcTime: Infinity,
    // Disable automatic refetching to prevent race conditions
    refetchOnMount: fresh ? 'always' : false,
    refetchOnWindowFocus: false,
    // No automatic refetch interval
    refetchInterval: false,
  });
}

// Create a new deal
export function useCreateDeal() {
  const axios = useAxios();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateDealInput) => {
      try {
        const { data } = await axios.post<Deal>(API_BASE_URL, input);
        return data;
      } catch (error) {
        throw new Error(getErrorMessage(error));
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: DEALS_QUERY_KEY });
    },
  });
}

// Update a deal
export function useUpdateDeal() {
  const axios = useAxios();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...input }: { id: string } & UpdateDealInput) => {
      try {
        const { data } = await axios.put<Deal>(`${API_BASE_URL}/${id}`, input);
        return data;
      } catch (error) {
        throw toVerificationConflict(error) ?? new Error(getErrorMessage(error));
      }
    },
    onMutate: async ({ id, ...input }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: [...DEALS_QUERY_KEY, id] });

      // Snapshot the previous value
      const previousDeal = queryClient.getQueryData<Deal>([...DEALS_QUERY_KEY, id]);

      // Optimistically update the cache
      if (previousDeal) {
        const updatedDeal: Deal = {
          ...previousDeal,
          ...input,
          // Ensure these nested objects maintain their correct types
          verification_document: input.verification_document === undefined
            ? previousDeal.verification_document
            : input.verification_document,
          registrants: input.registrants === undefined
            ? previousDeal.registrants // Keep existing for now as types don't match
            : previousDeal.registrants,
          verification_contacts: input.verification_contacts === undefined
            ? previousDeal.verification_contacts // Keep existing for now as types don't match
            : previousDeal.verification_contacts,
        };

        queryClient.setQueryData<Deal>([...DEALS_QUERY_KEY, id], updatedDeal);
      }

      // Return a context object with the snapshotted value
      return { previousDeal, id };
    },
    onError: (err, _variables, context) => {
      if (err instanceof VerificationConflictError) {
        queryClient.setQueryData<Deal>([...DEALS_QUERY_KEY, err.deal.id], err.deal);
        return;
      }
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousDeal) {
        queryClient.setQueryData<Deal>([...DEALS_QUERY_KEY, context.id], context.previousDeal);
      }
    },
    onSuccess: (data, { id }) => {
      // Update the cache with the server response
      queryClient.setQueryData<Deal>([...DEALS_QUERY_KEY, id], data);

      // Update the deals list cache if it exists
      queryClient.setQueryData<Deal[]>(DEALS_QUERY_KEY, (old) => {
        if (!old)
          return old;
        return old.map((deal) => (deal.id === id ? data : deal));
      });

      // Stage / outcome / archive changes affect whether the public verification link is active
      void queryClient.invalidateQueries({ queryKey: verificationLinkQueryKey(id) });
    },
  });
}

export type VerificationLinkBlockerCode
  = | 'deal_type_not_allowed'
    | 'outcome_set'
    | 'completed_stage'
    | 'archived'
    | 'verification_contact_submitted'
    | 'registrant_submitted'
    | 'stage_requires_submission';

export interface VerificationLinkBlockerTask {
  id: string;
  task: string;
  /** Same task text with the submission tag replaced so it no longer counts as a submission. */
  superseded_task: string;
}

export interface VerificationLinkBlocker {
  code: VerificationLinkBlockerCode;
  message: string;
  /** Which links this condition blocks. */
  affects: 'both' | 'registrant';
  /** For submission-based blockers, the tasks behind the submission. */
  tasks?: VerificationLinkBlockerTask[];
}

export interface VerificationLinkDetail {
  url: string;
  active: boolean;
  blockers: VerificationLinkBlockerCode[];
}

export interface VerificationLinkStatus {
  /** True only when both links are usable. */
  active: boolean;
  links: {
    registrant: VerificationLinkDetail;
    verification: VerificationLinkDetail;
  };
  blockers: VerificationLinkBlocker[];
  submissions: {
    registrant: boolean;
    verification_contact: boolean;
  };
  /** Stages the deal can be moved to without the stage itself blocking the link. */
  acceptable_stages: DealStage[];
}

export function verificationLinkQueryKey(id: string) {
  return [...DEALS_QUERY_KEY, id, 'verification-link'] as const;
}

// Status of the public verification link for a deal (mirrors the public endpoint's rules)
export function useVerificationLinkStatus(id: string | undefined, options?: { enabled?: boolean }) {
  const axios = useAxios();

  return useQuery({
    queryKey: verificationLinkQueryKey(id ?? ''),
    queryFn: async () => {
      try {
        const { data } = await axios.get<VerificationLinkStatus>(`${API_BASE_URL}/${id}/verification-link`);
        return data;
      } catch (error) {
        throw new Error(getErrorMessage(error));
      }
    },
    enabled: !!id && (options?.enabled ?? true),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
}

// Response type for recent deal by domain
export interface RecentDealResponse {
  id: string;
  created_at: string;
  deal_stage: string;
  deal_type: string;
  outcome: string | null;
  archived_at: string | null;
}

// Hook to get recent deal lookup function
export function useRecentDealByDomain() {
  const axios = useAxios();

  const getRecentDeal = async (domain: string): Promise<RecentDealResponse | null> => {
    try {
      const { data } = await axios.get<RecentDealResponse>(`${API_BASE_URL}/by-domain/${encodeURIComponent(domain)}`);
      return data;
    } catch {
      return null;
    }
  };

  return { getRecentDeal };
}
